import React, { useEffect, useRef, useState } from 'react';
import { Star, Trash2, MessageSquarePlus, Send, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { testimonials as fallback } from '../data/mock';
import { Button } from './ui/button';
import { toast } from 'sonner';

const getText = (item, lang) => typeof item.text === 'string' ? item.text : (lang === 'ar' ? item.text?.ar : item.text?.en) || '';
const getName = (item, lang) => typeof item.name === 'string' ? item.name : (lang === 'ar' ? item.name?.ar : item.name?.en) || '';
const getRole = (item, lang) => typeof item.role === 'string' ? item.role : (lang === 'ar' ? item.role?.ar : item.role?.en) || '';

const TestimonialCard = ({ item, isRTL, lang, canDelete, onDelete, onOpen }) => {
  const text = getText(item, lang);
  const name = getName(item, lang);
  const role = getRole(item, lang);
  const initials = (name || '?').trim().charAt(0).toUpperCase();
  const rating = item.rating || 5;
  return (
    <div
      onClick={() => onOpen(item)}
      data-testid={`testimonial-card-${item.id}`}
      className="cursor-pointer relative shrink-0 w-[290px] sm:w-[340px] rounded-2xl bg-white border border-slate-200/80 p-6 shadow-sm hover:border-blue-300 hover:-translate-y-1 hover:shadow-xl transition-[transform,box-shadow,border-color] duration-300 flex flex-col"
    >
      {canDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          data-testid={`delete-testimonial-${item.id}`}
          className="absolute top-3 left-3 w-8 h-8 rounded-full bg-red-500/20 hover:bg-red-500 text-red-600 hover:text-white flex items-center justify-center transition-colors border border-red-500/40 z-10"
          title={tx(lang, 'حذف التعليق', 'Delete comment')}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
      <div className={`flex items-center gap-1 mb-4 ${isRTL ? 'justify-end' : ''}`}>
        {Array.from({ length: rating }).map((_, i) => (
          <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
        ))}
      </div>
      <p className={`text-slate-600 text-sm leading-relaxed mb-6 flex-1 line-clamp-5 ${isRTL ? 'text-right' : 'text-left'}`}>
        “{text}”
      </p>
      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        {item.avatar ? (
          <img src={item.avatar} alt={name} className="w-11 h-11 rounded-full border-2 border-blue-100 object-cover" />
        ) : (
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 border-2 border-blue-100 flex items-center justify-center text-white font-bold">
            {initials}
          </div>
        )}
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <div className="text-slate-900 text-sm font-bold">{name}</div>
          <div className="text-slate-500 text-xs">{role}</div>
        </div>
      </div>
    </div>
  );
};

const TestimonialModal = ({ item, onClose, lang, isRTL }) => {
  if (!item) return null;
  const text = getText(item, lang);
  const name = getName(item, lang);
  const role = getRole(item, lang);
  const initials = (name || '?').trim().charAt(0).toUpperCase();
  const rating = item.rating || 5;
  return (
    <div onClick={onClose} className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200" data-testid="testimonial-modal">
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-2xl rounded-3xl bg-white border border-slate-200 p-6 md:p-8 shadow-2xl animate-in zoom-in-95 duration-200">
        <button onClick={onClose} className="absolute top-4 left-4 w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 flex items-center justify-center transition-colors" aria-label="close" data-testid="close-modal-btn">
          <X className="w-5 h-5" />
        </button>
        <div className={`flex items-center gap-1 mb-5 ${isRTL ? 'justify-end' : ''}`}>
          {Array.from({ length: rating }).map((_, i) => (
            <Star key={i} className="w-6 h-6 text-amber-400 fill-amber-400" />
          ))}
        </div>
        <p className={`text-slate-800 text-lg md:text-xl leading-relaxed mb-8 ${isRTL ? 'text-right' : 'text-left'}`}>
          “{text}”
        </p>
        <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
          {item.avatar ? (
            <img src={item.avatar} alt={name} className="w-16 h-16 rounded-full border-2 border-blue-500/60 object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 border-2 border-blue-500/60 flex items-center justify-center text-white text-2xl font-bold">
              {initials}
            </div>
          )}
          <div className={isRTL ? 'text-right' : 'text-left'}>
            <div className="text-slate-900 text-lg font-bold">{name}</div>
            <div className="text-slate-500 text-sm">{role}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddTestimonialForm = ({ onAdded, onCancel, lang, isRTL }) => {
  const [text, setText] = useState('');
  const [role, setRole] = useState('');
  const [rating, setRating] = useState(5);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/testimonials', { text: text.trim(), role: role.trim(), rating });
      toast.success(tx(lang, 'تم نشر تعليقك. شكراً!', 'Comment posted. Thanks!'));
      onAdded(data);
      setText(''); setRole(''); setRating(5);
    } catch (err) {
      toast.error(err?.response?.data?.detail || (tx(lang, 'تعذّر نشر التعليق', 'Could not post comment')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="testimonial-form" className="rounded-2xl bg-white border border-slate-200 p-6 mb-8 max-w-2xl mx-auto shadow-xl">
      <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <h3 className="text-slate-900 font-bold text-lg">{tx(lang, 'شاركنا تجربتك', 'Share your experience')}</h3>
        <button type="button" onClick={onCancel} className="text-slate-400 hover:text-slate-700" aria-label="close">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className={`flex items-center gap-1 mb-4 ${isRTL ? 'justify-end' : ''}`}>
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button" onClick={() => setRating(n)} className="p-1" aria-label={`rate ${n}`} data-testid={`star-${n}`}>
            <Star className={`w-7 h-7 transition-colors ${n <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} />
          </button>
        ))}
      </div>

      <textarea
        data-testid="testimonial-text-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        maxLength={600}
        required
        placeholder={tx(lang, 'اكتب رأيك عن المنصّة...', 'Write your opinion about the platform...')}
        className={`w-full px-4 py-3 rounded-xl bg-slate-50 border border-slate-200 focus:border-blue-500 text-slate-900 placeholder:text-slate-400 outline-none resize-none mb-3 ${isRTL ? 'text-right' : 'text-left'}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      />
      <input
        data-testid="testimonial-role-input"
        type="text"
        value={role}
        onChange={(e) => setRole(e.target.value)}
        maxLength={80}
        placeholder={tx(lang, 'مهنتك (اختياري) — مثال: مهندس تحكم', 'Your role (optional) — e.g. Control Engineer')}
        className={`w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-blue-500 text-slate-900 placeholder:text-slate-400 outline-none mb-4 text-sm ${isRTL ? 'text-right' : 'text-left'}`}
        dir={isRTL ? 'rtl' : 'ltr'}
      />
      <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Button type="submit" disabled={submitting || !text.trim()} className="btn-primary-blue border-0 h-11 px-5" data-testid="submit-testimonial-btn">
          <Send className="w-4 h-4 me-2" />
          {submitting ? (tx(lang, 'جارٍ النشر...', 'Posting...')) : (tx(lang, 'نشر التعليق', 'Post comment'))}
        </Button>
      </div>
    </form>
  );
};

const TestimonialsSection = () => {
  const { t, lang, isRTL } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [openItem, setOpenItem] = useState(null);
  const scrollerRef = useRef(null);
  const isAdmin = user?.role === 'admin';

  const load = async () => {
    try {
      const { data } = await api.get('/testimonials');
      // Combine API data with fallback mock for richer initial display
      const combined = [...(Array.isArray(data) ? data : []), ...fallback];
      setItems(combined);
    } catch {
      setItems(fallback);
    }
  };

  useEffect(() => { load(); }, []);

  const onAdded = (item) => {
    setItems(prev => [item, ...prev]);
    setShowForm(false);
  };

  const onDelete = async (id) => {
    if (!window.confirm(tx(lang, 'هل تريد حذف هذا التعليق؟', 'Delete this comment?'))) return;
    try {
      await api.delete(`/testimonials/${id}`);
      toast.success(tx(lang, 'تم الحذف', 'Deleted'));
      setItems(prev => prev.filter(x => x.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || (tx(lang, 'تعذّر الحذف', 'Could not delete')));
    }
  };

  const scrollByDir = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.85 * dir;
    el.scrollBy({ left: isRTL ? -amount : amount, behavior: 'smooth' });
  };

  return (
    <section data-testid="home-testimonials" className="section-y bg-slate-50">
      <div className="site-container">
        <div className="text-center section-head">
          <h2 className="text-2xl sm:text-3xl lg:text-[2rem] font-black text-slate-900 tracking-tight mb-2">{t.sections.testimonialsTitle}</h2>
          <p className="text-slate-500 text-sm md:text-base mb-6">{t.sections.testimonialsDesc}</p>

          {user ? (
            !showForm && (
              <Button onClick={() => setShowForm(true)} data-testid="open-testimonial-form" className="bg-slate-900 hover:bg-slate-800 text-white border-0 h-11 px-5 rounded-xl">
                <MessageSquarePlus className="w-4 h-4 me-2" />
                {tx(lang, 'أضف تعليقك', 'Add your comment')}
              </Button>
            )
          ) : (
            <div className="text-xs text-slate-400">
              {tx(lang, 'سجّل الدخول لإضافة تعليقك', 'Sign in to add your comment')}
            </div>
          )}
        </div>

        {showForm && user && (
          <AddTestimonialForm onAdded={onAdded} onCancel={() => setShowForm(false)} lang={lang} isRTL={isRTL} />
        )}

        {/* Horizontal scroller carousel - shows 16 testimonials */}
        <div className="relative">
          <button
            onClick={() => scrollByDir(-1)}
            data-testid="testimonials-prev"
            aria-label="prev"
            className={`flex absolute top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white hover:bg-blue-600 text-slate-700 hover:text-white items-center justify-center shadow-lg border border-slate-200 transition-colors ${isRTL ? 'right-0 md:right-2' : 'left-0 md:left-2'}`}
          >
            {isRTL ? <ChevronRight className="w-6 h-6" /> : <ChevronLeft className="w-6 h-6" />}
          </button>
          <button
            onClick={() => scrollByDir(1)}
            data-testid="testimonials-next"
            aria-label="next"
            className={`flex absolute top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white hover:bg-blue-600 text-slate-700 hover:text-white items-center justify-center shadow-lg border border-slate-200 transition-colors ${isRTL ? 'left-0 md:left-2' : 'right-0 md:right-2'}`}
          >
            {isRTL ? <ChevronLeft className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
          </button>

          <div
            ref={scrollerRef}
            data-testid="testimonials-scroller"
            onWheel={(e) => {
              if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.currentTarget.scrollLeft += e.deltaY;
              }
            }}
            className="flex flex-nowrap gap-5 overflow-x-auto pb-4 scroll-smooth px-14 md:px-16 testimonials-scroller"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            {items.map(item => (
              <TestimonialCard
                key={item.id}
                item={item}
                isRTL={isRTL}
                lang={lang}
                canDelete={isAdmin && typeof item.text === 'string'}
                onDelete={() => onDelete(item.id)}
                onOpen={(it) => setOpenItem(it)}
              />
            ))}
          </div>
        </div>

        <div className="text-center mt-4 text-xs text-slate-500">
          {lang === 'ar' ? `${items.length} تعليق · مرّر للجانب أو استخدم الأزرار · اضغط على البطاقة لقراءتها كاملة` : `${items.length} testimonials · swipe or use arrows · click a card to read it fully`}
        </div>
      </div>

      <TestimonialModal item={openItem} onClose={() => setOpenItem(null)} lang={lang} isRTL={isRTL} />
    </section>
  );
};

export default TestimonialsSection;
